import initSqlJs from "sql.js";
import wasmUrl from "sql.js/dist/sql-wasm.wasm?url";
import { OfflineSqliteDatabase, OfflineStore, ReconnectSyncEngine, type SqlJsStatic } from "../offline";
import type { OutboxOperation, SyncResponse, SyncTransport } from "../offline";
import { demoCustomers, demoProducts } from "./demoAdapter";
import { parseParkedBaskets } from "./baskets";
import type { CardReceipt, CartLine, ParkedBasket, PosDataAdapter, Receipt, SyncSnapshot } from "./types";

const moneyAmount = (minor: number) => new Intl.NumberFormat("en-GB", {
  style: "currency", currency: "GBP",
}).format(minor / 100);

// The terminal deliberately holds consecutive payments for up to 20 seconds
// while POSitive clears the preceding transaction. Leave a separate margin so
// CloudPOS cannot cancel a healthy queued payment at that exact boundary.
export const UNCLAIMED_PAYMENT_TIMEOUT_MS = 30_000;

export function unclaimedPaymentCountdownSeconds(
  state: string | undefined,
  elapsedMs: number,
): number | undefined {
  if (state?.trim().toUpperCase() !== "QUEUED") return undefined;
  return Math.max(0, Math.ceil((UNCLAIMED_PAYMENT_TIMEOUT_MS - elapsedMs) / 1000));
}

export function shouldCancelUnclaimedPayment(
  state: string | undefined,
  elapsedMs: number,
  cancellationRequested: boolean,
): boolean {
  return !cancellationRequested && state?.trim().toUpperCase() === "QUEUED" &&
    elapsedMs >= UNCLAIMED_PAYMENT_TIMEOUT_MS;
}

function transport(token: string): SyncTransport {
  return {
    async push(request): Promise<SyncResponse> {
      const headers = { "content-type": "application/json", authorization: `Bearer ${token}` };
      const response = await fetch("/api/sync/push", {
        method: "POST", headers,
        body: JSON.stringify({ mutations: request.operations.map(toMutation) }),
      });
      if (!response.ok) throw new Error(`Sync push failed (${response.status})`);
      const pushed = await response.json() as { accepted: Array<{ mutation_id?: string; mutationId?: string }> };
      const pull = await fetch(`/api/sync/pull?cursor=${encodeURIComponent(request.cursor ?? "0")}`, { headers });
      if (!pull.ok) throw new Error(`Sync pull failed (${pull.status})`);
      const pulled = await pull.json() as { changes: Array<Record<string, unknown>>; nextCursor: number };
      return {
        acknowledged: pushed.accepted.map((item) => String(item.mutationId ?? item.mutation_id)),
        cursor: String(pulled.nextCursor),
        changes: pulled.changes.map((change) => ({
          entityType: String(change.entityType), entityId: String(change.entityId), revision: Number(change.cursor),
          updatedAt: String(change.serverTimestamp), deleted: change.operation === "delete",
          data: (change.payload ?? undefined) as never,
        })),
      };
    },
  };
}

function toMutation(operation: OutboxOperation) {
  return { mutationId: operation.id, entityType: operation.entityType, entityId: operation.entityId,
    operation: operation.operation, clientTimestamp: operation.createdAt, payload: operation.payload };
}

export async function createLocalAdapter(): Promise<PosDataAdapter> {
  const SQL = await initSqlJs({ locateFile: () => wasmUrl });
  const database = await OfflineSqliteDatabase.open(SQL as unknown as SqlJsStatic);
  const store = new OfflineStore(database);
  const deviceToken = () => localStorage.getItem("u365_device_token");
  const PAYMENT_JOURNAL_KEY = "u365_cloudlink_active_payment";
  const PARKED_BASKETS_KEY = "u365_parked_baskets";
  type PaymentJournal = { referenceId: string; pollId?: string; amountMinor: number; state: string; lines?: CartLine[] };
  class PaymentActionError extends Error {
    readonly action = "mark_unpaid" as const;
  }
  const readPaymentJournal = (): PaymentJournal | undefined => {
    try {
      const value = JSON.parse(localStorage.getItem(PAYMENT_JOURNAL_KEY) ?? "null") as PaymentJournal | null;
      return value && typeof value.referenceId === "string" && Number.isSafeInteger(value.amountMinor) ? value : undefined;
    } catch { return undefined; }
  };
  const writePaymentJournal = (value?: PaymentJournal) => value
    ? localStorage.setItem(PAYMENT_JOURNAL_KEY, JSON.stringify(value))
    : localStorage.removeItem(PAYMENT_JOURNAL_KEY);
  const restoreJournalAsUnpaid = (journal: PaymentJournal, amountMinor = journal.amountMinor): ParkedBasket => {
    const now = new Date().toISOString();
    let lines = journal.lines?.length ? journal.lines : [{
      id: `unpaid-recovery-${journal.referenceId}`, name: "Unpaid basket recovery", category: "Recovery",
      price: amountMinor / 100, colour: "#f5ddd8", emoji: "↩", quantity: 1,
    }];
    // Journals written before catalogue prices became VAT-inclusive contain
    // net line prices while amountMinor contains the gross terminal request.
    // Upgrade only that exact 20% relationship; current journals are already
    // gross and therefore remain byte-for-byte unchanged.
    const storedLinesMinor = Math.round(lines.reduce((sum, line) => sum + line.price * line.quantity, 0) * 100);
    if (storedLinesMinor !== amountMinor && Math.round(storedLinesMinor * 1.2) === amountMinor) {
      lines = lines.map((line) => ({ ...line, price: Math.round(line.price * 120) / 100 }));
    }
    const basket: ParkedBasket = {
      id: `unpaid-${journal.referenceId}`.slice(0, 100), name: `UNPAID ${moneyAmount(amountMinor)}`,
      lines, createdAt: now, updatedAt: now, paymentState: "unpaid",
    };
    const baskets = parseParkedBaskets(localStorage.getItem(PARKED_BASKETS_KEY));
    localStorage.setItem(PARKED_BASKETS_KEY, JSON.stringify([
      ...baskets.filter((candidate) => candidate.id !== basket.id), basket,
    ]));
    window.dispatchEvent(new CustomEvent("u365:baskets-changed"));
    return basket;
  };
  let activePollId = readPaymentJournal()?.pollId;
  const listeners = new Set<(snapshot: SyncSnapshot) => void>();
  let syncing = false;
  let lastSyncedAt: string | undefined;
  const snapshot = (): SyncSnapshot => ({ online: navigator.onLine, syncing, pendingChanges: store.pendingCount(), lastSyncedAt });
  const emit = () => listeners.forEach((listener) => listener(snapshot()));
  const cloudReachable = async () => {
    if (!navigator.onLine) return false;
    try { return (await fetch("/health", { cache: "no-store" })).ok; }
    catch { return false; }
  };
  const initialToken = deviceToken();
  const engine = initialToken ? new ReconnectSyncEngine(store, transport(initialToken), {
    clientId: "browser-register", onError: () => { syncing = false; emit(); },
  }) : undefined;
  engine?.start();

  const adapter: PosDataAdapter = {
    async listProducts() { return demoProducts; },
    async listCustomers() { return demoCustomers; },
    async listReceipts(): Promise<Receipt[]> {
      return store.listSales().map((sale) => ({ id: sale.id, number: `#${sale.id.slice(0, 8)}`,
        createdAt: new Date(sale.createdAt).toLocaleString(), total: sale.totalMinor / 100,
        subtotal: sale.subtotalMinor / 100, tax: sale.taxMinor / 100,
        itemCount: sale.itemCount, payment: sale.payment, cardReceipt: sale.cardReceipt,
        lines: sale.items.map((item) => ({ productId: item.productId, name: item.name, quantity: item.quantity,
          unitPrice: item.unitPriceMinor / 100, total: item.lineTotalMinor / 100 })),
        syncState: sale.synced ? "synced" : "pending" }));
    },
    async checkout(input, onProgress) {
      const lookup = new Map(demoProducts.map((product) => [product.id, product]));
      const amountMinor = Math.round(input.lines.reduce((sum, line) => sum + line.quantity * line.unitPrice, 0) * 100);
      let paymentReference: string | undefined;
      let cardReceipt: CardReceipt | undefined;
      if (input.paymentMethod === "card") {
        const token = deviceToken();
        if (!token || !navigator.onLine) throw new Error("CloudLink card payment requires an online enrolled register");
        let journal = readPaymentJournal();
        if (journal?.pollId) {
          // Reconcile the durable server record before comparing baskets. A
          // cancelled/declined attempt must not strand the register forever,
          // while an unreceipted approval must continue to block a new charge.
          const recoveredResponse = await fetch(`/api/cloudlink/payments/${encodeURIComponent(journal.pollId)}`, {
            headers: { authorization: `Bearer ${token}` },
          });
          const recovered = await recoveredResponse.json().catch(() => ({})) as {
            state?: string; final?: boolean; approved?: boolean; consumed?: boolean;
          };
          if (recoveredResponse.ok) {
            if (recovered.final && recovered.approved && recovered.consumed) {
              writePaymentJournal();
              activePollId = undefined;
              journal = undefined;
            } else if (recovered.final && !recovered.approved) {
              // A terminal-confirmed cancellation/decline is safe to release:
              // preserve its original basket as UNPAID, clear the stale local
              // journal, and continue with the operator's current card sale.
              const overrideResponse = await fetch(`/api/cloudlink/payments/${encodeURIComponent(journal.pollId)}/mark-unpaid`, {
                method: "POST", headers: { authorization: `Bearer ${token}` },
              });
              const override = await overrideResponse.json().catch(() => ({})) as { amountMinor?: number };
              if (overrideResponse.ok) {
                const restoredAmount = Number.isSafeInteger(override.amountMinor) ? Number(override.amountMinor) : journal.amountMinor;
                restoreJournalAsUnpaid(journal, restoredAmount);
                writePaymentJournal();
                activePollId = undefined;
                journal = undefined;
              } else {
                journal = { ...journal, state: recovered.state ?? journal.state };
                writePaymentJournal(journal);
              }
            } else {
              journal = { ...journal, state: recovered.state ?? journal.state };
              writePaymentJournal(journal);
            }
          }
        }
        if (journal?.state === "APPROVED") {
          throw new Error(`Previous ${moneyAmount(journal.amountMinor)} card payment was approved but has no sales receipt. Reconcile it before taking another payment`);
        }
        if (journal) {
          const previous = moneyAmount(journal.amountMinor);
          throw new PaymentActionError(`Previous ${previous} terminal payment is ${journal.state.toLowerCase().replaceAll("_", " ")}. If the card machine did not complete it, return it to baskets as UNPAID`);
        }
        if (!journal) {
          const readiness = await fetch("/api/cloudlink/readiness", { headers: { authorization: `Bearer ${token}` } });
          const ready = await readiness.json() as { ready?: boolean; message?: string };
          if (!readiness.ok || !ready.ready) throw new Error(ready.message ?? "A920 CloudLink is not ready");
          journal = {
            referenceId: `cloudpos-${crypto.randomUUID()}`,
            amountMinor,
            state: "STARTING",
            lines: input.lines.map((line) => {
              const product = lookup.get(line.productId);
              return product
                ? { ...product, quantity: line.quantity }
                : { id: line.productId, name: "Recovered item", category: "Recovery", price: line.unitPrice,
                    colour: "#f5ddd8", emoji: "↩", quantity: line.quantity };
            }),
          };
          writePaymentJournal(journal);
        }
        const referenceId = journal.referenceId;
        const started = await fetch("/api/cloudlink/payments", { method: "POST", headers: {
          "content-type": "application/json", authorization: `Bearer ${token}`,
        }, body: JSON.stringify({ amountMinor, referenceId }) });
        const start = await started.json() as { pollId?: string; state?: string; statusMessage?: string; message?: string };
        if (!started.ok || !start.pollId) {
          if (started.status < 500) writePaymentJournal();
          throw new Error(start.message ?? "CloudLink payment could not start");
        }
        activePollId = start.pollId;
        journal = { ...journal, pollId: start.pollId, state: start.state ?? "QUEUED" };
        writePaymentJournal(journal);
        paymentReference = start.pollId;
        const paymentStartedAt = Date.now();
        const startedState = start.state ?? "QUEUED";
        onProgress?.({ pollId: start.pollId, state: startedState,
          statusMessage: start.statusMessage ?? "Payment queued",
          countdownSeconds: unclaimedPaymentCountdownSeconds(startedState, 0) });
        let unclaimedCancellationRequested = false;
        for (let attempt = 0; attempt < 180; attempt += 1) {
          await new Promise((resolve) => setTimeout(resolve, 1000));
          const response = await fetch(`/api/cloudlink/payments/${encodeURIComponent(start.pollId)}`, { headers: { authorization: `Bearer ${token}` } });
          const progress = await response.json() as { state?: string; statusMessage?: string; final?: boolean; approved?: boolean;
            terminalReceipt?: CardReceipt };
          if (!response.ok) throw new Error(progress.statusMessage ?? "CloudLink status unavailable");
          const elapsedMs = Date.now() - paymentStartedAt;
          onProgress?.({ pollId: start.pollId, state: progress.state ?? "ACTIVE",
            statusMessage: progress.statusMessage ?? "Processing", final: progress.final, approved: progress.approved,
            countdownSeconds: unclaimedPaymentCountdownSeconds(progress.state, elapsedMs) });
          if (progress.final) {
            activePollId = undefined;
            if (!progress.approved) {
              writePaymentJournal();
              throw new Error(progress.statusMessage ?? "Card payment was not approved");
            }
            cardReceipt = progress.terminalReceipt;
            writePaymentJournal({ ...journal, state: "APPROVED" });
            break;
          }
          if (shouldCancelUnclaimedPayment(
            progress.state,
            elapsedMs,
            unclaimedCancellationRequested,
          )) {
            // QUEUED is canonical proof that the terminal has not claimed the
            // request. Cancelling here cannot interrupt an acknowledged/card-
            // processing payment; every claimed phase is deliberately excluded.
            unclaimedCancellationRequested = true;
            const cancellation = await fetch(`/api/cloudlink/payments/${encodeURIComponent(start.pollId)}/cancel-unclaimed`, {
              method: "POST", headers: { authorization: `Bearer ${token}` },
            });
            if (cancellation.ok) {
              journal = { ...journal, state: "CANCELLING" };
              writePaymentJournal(journal);
              onProgress?.({ pollId: start.pollId, state: "CANCELLING",
                statusMessage: "Card machine did not claim the payment within 20 seconds; cancelling safely",
                countdownSeconds: 0 });
            }
          }
          if (attempt === 179) throw new Error("Card payment status timed out; check the A920 before retrying");
        }
      }
      const sale = await store.createSale({ terminalId: "register-1", currency: "GBP",
        items: input.lines.map((line) => {
          const grossUnitMinor = Math.round(line.unitPrice * 100);
          const netUnitMinor = Math.round(grossUnitMinor / 1.2);
          return { productId: line.productId, name: lookup.get(line.productId)?.name ?? "Item",
            quantity: line.quantity, unitPriceMinor: netUnitMinor,
            taxMinor: line.quantity * (grossUnitMinor - netUnitMinor) };
        }),
        payments: [{ method: input.paymentMethod, amountMinor, reference: paymentReference, cardReceipt }],
      });
      if (input.paymentMethod === "card") writePaymentJournal();
      emit();
      return { id: sale.id, number: `#${sale.id.slice(0, 8)}`, createdAt: "Just now", total: sale.totalMinor / 100,
        subtotal: sale.subtotalMinor / 100, tax: sale.taxMinor / 100,
        itemCount: sale.items.reduce((sum, item) => sum + item.quantity, 0), payment: input.paymentMethod === "card" ? "Card" : "Cash",
        lines: sale.items.map((item) => {
          const lineTotalMinor = Math.round(item.quantity * item.unitPriceMinor) + item.taxMinor;
          return { productId: item.productId, name: item.name, quantity: item.quantity,
            unitPrice: Math.round(lineTotalMinor / item.quantity) / 100, total: lineTotalMinor / 100 };
        }),
        cardReceipt, syncState: "pending" };
    },
    async cancelPayment() {
      const token = deviceToken();
      if (!token || !activePollId) return;
      await fetch(`/api/cloudlink/payments/${encodeURIComponent(activePollId)}/cancel`, { method: "POST", headers: { authorization: `Bearer ${token}` } });
    },
    async returnPreviousPaymentUnpaid() {
      const token = deviceToken();
      const journal = readPaymentJournal();
      if (!token || !navigator.onLine) throw new Error("An internet connection is required for the unpaid override");
      if (!journal?.pollId) throw new Error("No previous terminal payment is available to return");
      const response = await fetch(`/api/cloudlink/payments/${encodeURIComponent(journal.pollId)}/mark-unpaid`, {
        method: "POST", headers: { authorization: `Bearer ${token}` },
      });
      const result = await response.json().catch(() => ({})) as { error?: string; message?: string; amountMinor?: number };
      if (!response.ok) {
        if (response.status === 409) {
          const statusResponse = await fetch(`/api/cloudlink/payments/${encodeURIComponent(journal.pollId)}`, {
            headers: { authorization: `Bearer ${token}` },
          });
          const status = await statusResponse.json().catch(() => ({})) as { approved?: boolean; consumed?: boolean };
          if (statusResponse.ok && status.approved && status.consumed) {
            writePaymentJournal();
            activePollId = undefined;
            return { amountMinor: journal.amountMinor, reconciled: true };
          }
          throw new Error("This card payment is approved or already receipted and cannot be marked unpaid");
        }
        throw new Error(result.message ?? "The unpaid override could not be recorded");
      }
      const amountMinor = Number.isSafeInteger(result.amountMinor) ? Number(result.amountMinor) : journal.amountMinor;
      const basket = restoreJournalAsUnpaid(journal, amountMinor);
      writePaymentJournal();
      activePollId = undefined;
      return { basket, amountMinor };
    },
    isEnrolled() { return Boolean(deviceToken()); },
    async enrollRegister(value: string) {
      const raw = value.trim();
      const candidate = raw.length === 8 ? raw.toUpperCase() : raw;
      if (!/^[A-Z0-9]{8}$/.test(candidate) && (candidate.length < 32 || candidate.length > 512)) {
        throw new Error("Enter the 8-character register code");
      }
      const response = await fetch("/api/cloudlink/readiness", { headers: { authorization: `Bearer ${candidate}` } });
      if (response.status === 401) throw new Error("The register token was not accepted");
      if (!response.ok && response.status !== 409) throw new Error("Register enrolment could not be verified");
      localStorage.setItem("u365_device_token", candidate);
      window.location.reload();
    },
    async requestSync() {
      if (!engine || !(await cloudReachable())) return;
      syncing = true; emit();
      try { await engine.flush(); lastSyncedAt = new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }); }
      finally { syncing = false; emit(); }
    },
    subscribeSync(listener) {
      listeners.add(listener); listener(snapshot());
      const onConnectivity = () => { emit(); if (navigator.onLine && engine) void adapter.requestSync(); };
      window.addEventListener("online", onConnectivity); window.addEventListener("offline", onConnectivity);
      return () => { listeners.delete(listener); window.removeEventListener("online", onConnectivity); window.removeEventListener("offline", onConnectivity); };
    },
  };
  // One bounded probe handles an app reopened after connectivity returned.
  // No timer runs while offline; later attempts are driven by the browser's
  // online event or an explicit cashier action.
  if (engine && navigator.onLine) queueMicrotask(() => void adapter.requestSync());
  return adapter;
}
